import { Link } from 'react-router-dom';
import { OriginalsNav } from '@/components/OriginalsNav';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Trash2, Plus, Minus, ArrowRight, ShoppingBag } from 'lucide-react';

export default function OriginalsCart() {
  const { cart, removeFromCart, updateQuantity, getTotal, clearCart } = useCart();

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]" data-testid="cart-empty">
        <OriginalsNav />
        <div className="pt-32 pb-20 px-6 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-24 h-24 rounded-full border border-[#27272a] flex items-center justify-center mx-auto mb-8">
              <ShoppingBag className="w-10 h-10 text-[#a1a1aa]" />
            </div>
            <h1 className="font-playfair text-3xl text-white mb-4">Your Cart is Empty</h1>
            <p className="font-cormorant text-lg text-[#a1a1aa] mb-8">
              Discover unique metal art pieces in our gallery
            </p>
            <Link 
              to="/originals"
              className="btn-originals inline-flex items-center gap-3 px-8 py-4 rounded-full font-cinzel tracking-wider"
              data-testid="browse-gallery-btn"
            >
              Browse Gallery
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="originals-cart">
      <OriginalsNav />
      
      <main className="pt-28 pb-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h1 className="font-playfair text-3xl lg:text-4xl text-white mb-12">Your Cart</h1>

          {/* Cart Items */}
          <div className="space-y-6 mb-12">
            {cart.map((item, index) => {
              // Create unique key for items with options
              const itemKey = item.selectedOptions && Object.keys(item.selectedOptions).length > 0
                ? `${item.product.id}-${JSON.stringify(item.selectedOptions)}`
                : item.product.id;
              
              return (
                <div 
                  key={itemKey}
                  className="flex gap-6 p-6 bg-[#171717] rounded-lg border border-[#27272a]"
                  data-testid={`cart-item-${index}`}
                >
                  {/* Product Image */}
                  <Link to={`/originals/product/${item.product.id}`} className="flex-shrink-0">
                    <img 
                      src={item.product.image_url} 
                      alt={item.product.name}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                  </Link>

                  {/* Product Details */}
                  <div className="flex-grow">
                    <Link 
                      to={`/originals/product/${item.product.id}`}
                      className="font-playfair text-lg text-white hover:text-[#d4af37] transition-colors"
                    >
                      {item.product.name}
                    </Link>
                    
                    {/* Display Selected Options */}
                    {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {Object.entries(item.selectedOptions).map(([key, value]) => (
                          <p key={key} className="font-cormorant text-sm text-[#a1a1aa] capitalize">
                            {key}: <span className="text-white">{value}</span>
                          </p>
                        ))}
                      </div>
                    )}
                    
                    <p className="font-mono text-[#d4af37] mt-1">
                      £{Math.floor(item.product.price)}
                    </p>

                    {/* Quantity Controls */}
                    <div className="flex items-center gap-3 mt-4">
                      <div className="flex items-center border border-[#27272a] rounded-full">
                        <button 
                          onClick={() => updateQuantity(item.product.id, item.quantity - 1, item.selectedOptions)}
                          className="p-2 hover:text-[#d4af37] transition-colors"
                          data-testid={`decrease-${index}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="font-mono w-8 text-center text-sm">{item.quantity}</span>
                        <button 
                          onClick={() => updateQuantity(item.product.id, item.quantity + 1, item.selectedOptions)}
                          className="p-2 hover:text-[#d4af37] transition-colors"
                          data-testid={`increase-${index}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <button 
                        onClick={() => removeFromCart(item.product.id, item.selectedOptions)}
                        className="p-2 text-[#a1a1aa] hover:text-red-500 transition-colors"
                        data-testid={`remove-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="font-mono text-lg text-white">
                      £{Math.floor(item.product.price * item.quantity)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Cart Summary */}
          <div className="bg-[#171717] rounded-lg border border-[#27272a] p-8">
            <div className="flex justify-between items-center mb-6">
              <span className="font-cormorant text-lg text-[#a1a1aa]">Subtotal</span>
              <span className="font-mono text-2xl text-white">£{Math.floor(getTotal())}</span>
            </div>
            
            <p className="font-cormorant text-sm text-[#a1a1aa] mb-8">
              Shipping and taxes calculated at checkout
            </p>

            <div className="space-y-4">
              <Link to="/originals/checkout" data-testid="checkout-btn">
                <Button className="w-full py-6 rounded-full bg-[#d4af37] text-black hover:bg-[#cd7f32] font-cinzel tracking-wider">
                  Proceed to Checkout
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              
              <div className="flex gap-4">
                <Link 
                  to="/originals"
                  className="flex-1 py-3 text-center border border-[#27272a] rounded-full font-cormorant text-[#a1a1aa] hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
                >
                  Continue Shopping
                </Link>
                <button 
                  onClick={clearCart}
                  className="flex-1 py-3 text-center border border-[#27272a] rounded-full font-cormorant text-[#a1a1aa] hover:border-red-500 hover:text-red-500 transition-colors"
                  data-testid="clear-cart-btn"
                >
                  Clear Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#27272a] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="font-cormorant text-sm text-[#a1a1aa] text-center">
            © 2024 Ben Jefferies Originals. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
